import React from 'react';

import Title from 'lib/src/components/typography/Title';

import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import Breadcrumb from '@components/breadcrumb/Breadcrumb';
import { singleChallengeApplicationBreadcrumb } from '../../../constants/breadcrumbItems';
import { useParams } from 'react-router';
import useSingleUserApplication from './_hooks/useSingleUserApplication';
import DataCheck from 'lib/src/components/common/DataCheck';
import { formatMoney, isEmpty } from 'lib/src/utils/generic';
import dayjs from 'dayjs';

const UserApplication: React.FC = () => {
    const { id } = useParams<ParamTypes>();
    const { application, isFetching, fetchError } = useSingleUserApplication(+id);

    return (
        <>
            <DataCheck
                dataExists={!isEmpty(application)}
                isFetching={isFetching}
                error={fetchError}
            >
                <Breadcrumb
                    items={singleChallengeApplicationBreadcrumb}
                    title={application.companyName || undefined}
                />
                <Title>
                    Application Details
                    {application.challengeName ? ' - ' + application.challengeName : ''}
                </Title>

                <ContentBlock>
                    <ContentRow>
                        <ContentItem label="Company / Organisation name">
                            <p>{application.companyName || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Website">
                            <p>
                                {application.url ? (
                                    <a
                                        target="_blank"
                                        href={application.url}
                                        rel="noopener noreferrer"
                                    >
                                        {application.url}
                                    </a>
                                ) : (
                                    '-'
                                )}
                            </p>
                        </ContentItem>
                        <ContentItem label="Application date">
                            <p>
                                {!!application.createdOn
                                    ? dayjs(application.createdOn).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Address line 1">
                            <p>{application.addressLine1 || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Address line 2">
                            <p>{application.addressLine2 || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Town/City">
                            <p>{application.townCity || '-'}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Postcode">
                            <p>{application.postcode || '-'}</p>
                        </ContentItem>
                        <ContentItem label="County">
                            <p>{application.county || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Region">
                            <p>{application.region || '-'}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Contact name">
                            <p>{application.contactName || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Job title">
                            <p>{application.jobTitle || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Email">
                            <p>
                                {application.email ? (
                                    <a href={`mailto:${application.email}`}>{application.email}</a>
                                ) : (
                                    '-'
                                )}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Telephone">
                            <p>{application.telephone || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Company number">
                            <p>{application.companyReg || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Turnover">
                            <p>
                                {!!application.turnover
                                    ? '£' + formatMoney(+application.turnover)
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                </ContentBlock>
                <Title>Idea Summary</Title>
                <ContentBlock>
                    <ContentItem label={''}>
                        <p>
                            {!!application.isSummaryComplete
                                ? application.ideaSummary
                                : 'This section is not complete'}
                        </p>
                    </ContentItem>
                </ContentBlock>
                <Title>Technology Readiness</Title>
                <ContentBlock>
                    <ContentItem label={''}>
                        <p>
                            {!!application.isTRLComplete
                                ? application.trlDescription
                                : 'This section is not complete'}
                        </p>
                    </ContentItem>
                </ContentBlock>
                <Title>Intellectual Property</Title>
                <ContentBlock>
                    <ContentItem label={''}>
                        <p>
                            {!!application.isIPStatusComplete
                                ? application.ipStatusDescription
                                : 'This section is not complete'}
                        </p>
                    </ContentItem>
                </ContentBlock>
                <Title>Extra Attachments</Title>
                <ContentBlock>
                    <ContentItem label={''}>
                        <p>
                            {!!application.isExtraAttachmentsComplete
                                ? '### TO DO ###'
                                : 'This section is not complete'}
                        </p>
                    </ContentItem>
                </ContentBlock>
            </DataCheck>
        </>
    );
};

interface ParamTypes {
    id: string;
}

export default UserApplication;
